import { useLanguage } from './language-context';
import { useTheme } from './theme-context';
import { NavIcon } from './nav-icon';
import { appEnvironment, envPillFor } from './app-build';
import { initialsOf } from './initials-of';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { localiseDigits } from '@bookingdei/i18n/format-number';
import type { MessageKey } from '@bookingdei/i18n/message-key';

// CB-17 top bar (design D:225-259, script D:8595-8600): sticky glass header with
// the ⌘K search pill, the env pill (build config — shown off production only),
// the light/dark track, the bell with its unread count, and the account pill.
// The design's "?" help button is omitted: PRD §14.1 row 12 defers it with the
// help centre and its contact/escalation sheet (DR-43, DC110). Overlays (palette, tray, account menu) are owned by dash-layout.tsx;
// this file only renders their triggers.

export function TopBar({
  userName,
  roleLabel,
  unread,
  onSearch,
  onBell,
  onAccount,
  bellOpen,
  accountOpen,
}: {
  userName: string | undefined;
  roleLabel: string;
  unread: number;
  onSearch: () => void;
  onBell: () => void;
  onAccount: () => void;
  bellOpen: boolean;
  accountOpen: boolean;
}) {
  const { t, lang } = useLanguage();
  const { resolved, setPref } = useTheme();
  const env = envPillFor(appEnvironment);
  const dark = resolved === 'dark';
  const envLabel = env ? t(`admin.shell.env.${env}` as MessageKey) : '';
  const bellLabel = unread > 0 ? formatMessage(lang, 'admin.shell.notifications_unread', { n: unread }) : t('admin.shell.notifications');

  return (
    <header className="top-bar">
      <button type="button" className="top-bar__search" onClick={onSearch} aria-label={t('admin.shell.open_search')} aria-keyshortcuts="Meta+K Control+K">
        <span className="top-bar__search-icon">
          <NavIcon name="search" size={15} />
        </span>
        <span className="top-bar__search-text">{t('admin.shell.search_placeholder')}</span>
        <kbd className="top-bar__kbd">⌘K</kbd>
      </button>

      <div className="top-bar__tools">
        {env && (
          <span className="top-bar__env" title={formatMessage(lang, 'admin.shell.env.title', { env: envLabel })}>
            <span className="top-bar__env-dot" aria-hidden="true" />
            {envLabel}
          </span>
        )}

        <button
          type="button"
          className={dark ? 'top-bar__theme is-dark' : 'top-bar__theme'}
          onClick={() => setPref(dark ? 'light' : 'dark')}
          aria-label={t(dark ? 'theme.switch_to_light' : 'theme.switch_to_dark')}
          title={t(dark ? 'theme.switch_to_light' : 'theme.switch_to_dark')}
        >
          <span className="top-bar__theme-knob">
            <NavIcon name={dark ? 'moon' : 'sun'} size={13} />
          </span>
          <span className="top-bar__theme-ghost">
            <NavIcon name={dark ? 'sun' : 'moon'} size={13} />
          </span>
        </button>

        <div className="top-bar__group">
          <button type="button" className="top-bar__bell" onClick={onBell} aria-label={bellLabel} aria-haspopup="dialog" aria-expanded={bellOpen}>
            {unread > 0 && (
              <span className="top-bar__bell-count" aria-hidden="true">
                {localiseDigits(lang, unread)}
              </span>
            )}
            <NavIcon name="bell" size={17} />
          </button>
        </div>

        <button
          type="button"
          className="top-bar__acct"
          onClick={onAccount}
          aria-label={t('admin.shell.account')}
          aria-haspopup="dialog"
          aria-expanded={accountOpen}
        >
          <span className="top-bar__acct-avatar" aria-hidden="true">
            {initialsOf(userName)}
          </span>
          <span className="top-bar__acct-text">
            <span className="top-bar__acct-name">{userName}</span>
            <span className="top-bar__acct-role">{roleLabel}</span>
          </span>
          <span className={accountOpen ? 'top-bar__acct-chev is-open' : 'top-bar__acct-chev'} aria-hidden="true">
            ⌄
          </span>
        </button>
      </div>
    </header>
  );
}
